import { defineRouting } from "next-intl/routing"

import { DEFAULT_LOCALE, LOCALE_COOKIE, LOCALE_PREFIXES, LOCALES, servedLocales, SUPPORTED_LOCALES, type Locale } from "./locales"

const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

const routingFor = (locales: readonly Locale[]) =>
  defineRouting({
    locales,
    defaultLocale: DEFAULT_LOCALE,
    // The prefix leads the path (/en/…, served internally as /en/mx/…) because that is the only
    // shape next-intl rewrites.
    localePrefix: { mode: "as-needed", prefixes: LOCALE_PREFIXES },
    localeCookie: { name: LOCALE_COOKIE, maxAge: ONE_YEAR_SECONDS, sameSite: "lax", secure: process.env.NODE_ENV === "production" },
    // The URL decides the language. Accept-Language would otherwise send a Mexican
    // shopper whose phone is set to English to the English storefront.
    localeDetection: false,
    // The proxy hands next-intl the internal /<cc>/… path, so its Link header would advertise
    // hreflang URLs carrying the region segment the store no longer serves. Pages emit their
    // own hreflang through generateMetadata (localeAlternates).
    alternateLinks: false,
  })

export const routing = routingFor(LOCALES)

// Staff sections serve every catalog; see servedLocales.
export const staffRouting = routingFor(SUPPORTED_LOCALES)

/** @param path - without a locale prefix, with or without a region; decides which locales are served there */
export const canonicalPrefix = (locale: Locale, path = ""): string => (servedLocales(path).includes(locale) ? (LOCALE_PREFIXES[locale] ?? "") : "")

// Every supported locale is matched, served or not, so an unserved prefix can be redirected away.
// The raw codes are here because a hand-typed or machine-generated URL carries /es-MX/… .
const PREFIXED_LOCALES: [string, Locale][] = [
  ...Object.entries(LOCALE_PREFIXES).map(([locale, prefix]): [string, Locale] => [prefix, locale as Locale]),
  ...SUPPORTED_LOCALES.map((locale): [string, Locale] => [`/${locale}`, locale]),
]

export const splitLocalePrefix = (pathname: string): { prefix: string; locale: Locale; rest: string } => {
  for (const [prefix, locale] of PREFIXED_LOCALES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return { prefix, locale, rest: pathname.slice(prefix.length) || "/" }
  }

  return { prefix: "", locale: DEFAULT_LOCALE, rest: pathname }
}
